import React, { useState } from "react";
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useToast,
  Link,
} from "@chakra-ui/react";
import { useAuth } from "../context/AuthContext";
import bmLogo from "../assets/svg/bm_logo.svg";
import signInImage from "../assets/img/signInImage.png";
import Footer from "../components/Footer/Footer";
import GradientBorder from "../components/GradientBorder/GradientBorder";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const titleColor = "white";
  const textColor = "gray.400";
  const toast = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { resetPassword, checkLoginMethods } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      // Check login methods first
      const methods = await checkLoginMethods(email);
      
      if (methods.includes("google.com")) {
        toast({
          title: t("accountUsesGoogle"),
          description: t("pleaseLoginWithGoogle"),
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      // Send reset email
      await resetPassword(email);
      setSuccess(true);
      toast({
        title: t("emailSent"),
        description: t("checkInboxForResetLink"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error(error);
      let errorMessage = t("unexpectedError");
      if (error.code === 'auth/user-not-found') {
        errorMessage = t("userNotFound");
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = t("invalidEmail");
      }
      
      toast({
        title: t("error"),
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex position="relative">
      <Flex
        minH="100vh"
        h={{ base: "auto", lg: "100vh" }}
        w="100%"
        maxW="1044px"
        mx="auto"
        pt={{ sm: "100px", md: "0px" }}
        flexDirection="column"
        me={{ base: "auto", lg: "50px", xl: "auto" }}
      >
        <Flex
          alignItems="center"
          justifyContent="start"
          style={{ userSelect: "none" }}
          mx={{ base: "auto", lg: "unset" }}
          ms={{ base: "auto", lg: "auto" }}
          w={{ base: "100%", md: "50%", lg: "450px" }}
          px="50px"
        >
          <Flex
            direction="column"
            w="100%"
            background="transparent"
            mt={{ base: "50px", md: "150px", lg: "50px", xl: "50px" }}
            mb={{ base: "60px", lg: "95px" }}
            alignItems="center"
          >
            <Box alignItems="left" w="100%">
              <img
                src={bmLogo}
                alt={t('bmLogo')}
                width="200px"
                style={{ marginBottom: "20px" }}
              />
              <Heading color={titleColor} fontSize="32px" mb="10px">
                {t("forgotPasswordTitle")}
              </Heading>
              <Text
                mb="36px"
                ms="4px"
                color={textColor}
                fontWeight="bold"
                fontSize="14px"
              >
                {t("enterEmailToReset")}
              </Text>
            </Box>

            {success ? (
              <Box w="100%" textAlign="center">
                <Text color="green.400" mb={4} fontWeight="bold">
                  {t("emailSentSuccess")}
                </Text>
                <Button
                    colorScheme="primary"
                    fontSize="15px"
                    w="100%"
                    maxW="350px"
                    h="45"
                    mb="20px"
                    onClick={() => navigate("/login")}
                >
                    {t("backToLogin")}
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <FormControl mb="24px">
                  <FormLabel ms="4px" fontSize="sm" fontWeight="normal" color="white">
                    {t("email")}
                  </FormLabel>
                  <GradientBorder
                    w={{ base: "100%", lg: "fit-content" }}
                    borderRadius="20px"
                  >
                    <Input
                      color="white"
                      bg="rgb(19,21,54)"
                      border="transparent"
                      borderRadius="20px"
                      fontSize="sm"
                      size="lg"
                      w={{ base: "100%", md: "346px" }}
                      maxW="100%"
                      h="46px"
                      type="email"
                      placeholder={t("emailPlaceholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </GradientBorder>
                </FormControl>

                <Button
                  type="submit"
                  variant="brand"
                  fontSize="15px"
                  w="100%"
                  maxW="350px"
                  h="45"
                  mb="24px"
                  color="white"
                  bg="teal.300"
                  _hover={{ bg: "primary.200" }}
                  _active={{ bg: "primary.400" }}
                  isLoading={loading}
                >
                  {t("sendResetLink")}
                </Button>

                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    maxW="100%"
                    mt="0px"
                >
                    <Text color={textColor} fontWeight="medium">
                        <Link
                            color={titleColor}
                            as="span"
                            ms="5px"
                            fontWeight="bold"
                            onClick={() => navigate("/login")}
                            cursor="pointer"
                        >
                            {t("backToLogin")}
                        </Link>
                    </Text>
                </Flex>
              </form>
            )}
          </Flex>
        </Flex>
        
        <Box
          w={{ base: "335px", md: "450px" }}
          mx={{ base: "auto", lg: "unset" }}
          ms={{ base: "auto", lg: "auto" }}
          mb="80px"
        >
          <Footer />
        </Box>

        <Box
          display={{ base: "none", lg: "block" }}
          overflowX="hidden"
          h="100%"
          maxW={{ md: "50vw", lg: "50vw" }}
          minH="100vh"
          w="960px"
          position="absolute"
          left="0px"
        >
          <Box
            bgImage={signInImage}
            w="100%"
            h="100%"
            bgSize="cover"
            bgPosition="50%"
            position="absolute"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Text
              textAlign="center"
              color="white"
              letterSpacing="8px"
              fontSize="20px"
              fontWeight="500"
            >
              {t("levelUp")}
            </Text>
            <Text
              textAlign="center"
              color="transparent"
              letterSpacing="8px"
              fontSize="36px"
              fontWeight="bold"
              bgClip="text !important"
              bg="linear-gradient(94.56deg, #FFFFFF 79.99%, #21242F 102.65%)"
            >
              {t("withBulletMonkey")}
            </Text>
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
};

export default ForgotPassword;
